import apiClientService from "./ApiClientService";

/**
 * POST /notifications/confirm/{token}
 */
export async function confirmReservationPresence(token) {
   const safe = encodeURIComponent(String(token || ""));
   // NU mai pune /api aici, ApiClientService îl are deja
   const res = await apiClientService.post(`/notifications/confirm/${safe}`);

   if (!res.ok) {
      // încearcă să citești body-ul de eroare (dacă există)
      let msg = "Nu s-a putut confirma prezența.";
      try {
         const err = await res.json();
         if (err?.message) msg = err.message;
      } catch {}
      throw new Error(msg);
   }

   // 201 poate să nu aibă body → protejăm parsarea
   try {
      return await res.json();
   } catch {
      return {};
   }
}

/**
 * POST /notifications/test-tomorrow
 */
export async function testNotificationsTomorrow() {
   const res = await apiClientService.post(`/notifications/test-tomorrow`);

   if (!res.ok) {
      let msg = "Nu s-au putut trimite notificările de test.";
      try {
         const err = await res.json();
         if (err?.message) msg = err.message;
      } catch {}
      throw new Error(msg);
   }

   try {
      return await res.json();
   } catch {
      return {};
   }
}

async function parseJsonSafe(res) {
   try {
      return await res.json();
   } catch {
      return {};
   }
}

async function throwFromResponse(res, fallbackMsg) {
   let msg = fallbackMsg;
   try {
      const err = await res.json();
      if (err?.message) {
         msg = Array.isArray(err.message) ? err.message.join(" ") : err.message;
      }
   } catch {}
   throw new Error(msg);
}

/**
 * Normalizează răspunsul paginat al backend-ului la o formă stabilă.
 * Acceptă array simplu sau obiect cu {items|data|results|rows|notifications}.
 */
export function normalizeNotificationsPage(raw) {
   if (Array.isArray(raw)) {
      return { items: raw, total: raw.length, page: 1, limit: raw.length };
   }
   if (!raw || typeof raw !== "object") {
      return { items: [], total: 0, page: 1, limit: 0 };
   }

   const items =
      raw.items ||
      raw.data ||
      raw.results ||
      raw.rows ||
      raw.notifications ||
      raw.list ||
      [];

   const list = Array.isArray(items) ? items : [];

   const total = Number(
      raw.total ?? raw.count ?? raw.totalCount ?? raw.totalItems ?? list.length,
   );
   const page = Number(raw.page ?? raw.currentPage ?? 1);
   const limit = Number(raw.limit ?? raw.perPage ?? raw.pageSize ?? list.length);

   return {
      items: list,
      total: Number.isFinite(total) ? total : list.length,
      page: Number.isFinite(page) ? page : 1,
      limit: Number.isFinite(limit) ? limit : list.length,
   };
}

/**
 * GET /notifications — lista notificărilor proprii, paginată.
 */
export async function getNotifications({
   page = 1,
   limit = 20,
   unreadOnly = false,
} = {}) {
   const qs = new URLSearchParams();
   qs.set("page", String(page));
   qs.set("limit", String(limit));
   qs.set("unreadOnly", String(Boolean(unreadOnly)));

   const res = await apiClientService.get(`/notifications?${qs.toString()}`);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-au putut încărca notificările.");
   }
   return normalizeNotificationsPage(await parseJsonSafe(res));
}

/**
 * GET /notifications/unread-count — numărul de notificări necitite.
 */
export async function getUnreadNotificationsCount() {
   const res = await apiClientService.get(`/notifications/unread-count`);
   if (!res.ok) {
      await throwFromResponse(
         res,
         "Nu s-a putut obține numărul de notificări necitite.",
      );
   }
   const data = await parseJsonSafe(res);
   if (typeof data === "number") return data;
   const value = Number(
      data?.count ?? data?.unread ?? data?.unreadCount ?? data?.total ?? 0,
   );
   return Number.isFinite(value) ? value : 0;
}

/**
 * PATCH /notifications/{id}/read — marchează o notificare ca citită.
 */
export async function markNotificationRead(id) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.patch(`/notifications/${safe}/read`);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut marca notificarea ca citită.");
   }
   return parseJsonSafe(res);
}

/**
 * PATCH /notifications/read-all — marchează toate notificările ca citite.
 */
export async function markAllNotificationsRead() {
   const res = await apiClientService.patch(`/notifications/read-all`);
   if (!res.ok) {
      await throwFromResponse(
         res,
         "Nu s-au putut marca notificările ca citite.",
      );
   }
   return parseJsonSafe(res);
}

/**
 * POST /notifications/document-ready — notifică studentul că un document
 * e gata de ridicare (Admin/Manager).
 */
export async function notifyDocumentReady({ userId, message }) {
   const res = await apiClientService.post(`/notifications/document-ready`, {
      userId: Number(userId),
      message: String(message || ""),
   });
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut trimite notificarea.");
   }
   return parseJsonSafe(res);
}
